import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";
import {
  Shell,
  EmptyState,
  formatMoney,
  formatDate,
} from "./Common";
import { FONT_HEADING } from "../../../theme";

// Inline Shopify-shaped icon — green tile with a stylized "S".
function ShopifyMark({ size = 44 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        bgcolor: "#95BF47",
        borderRadius: "10px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_HEADING,
          fontSize: size * 0.55,
          fontWeight: 800,
          color: "#fff",
          lineHeight: 1,
          fontStyle: "italic",
        }}
      >
        S
      </Typography>
    </Box>
  );
}

// Small info badge used in the row beneath the line-items table.
// (icon + label + value)
function InfoBadge({ icon, label, value, tone = "blue" }) {
  const tones = {
    blue: { bg: "#dbeafe", color: "#1d4ed8" },
    purple: { bg: "#ede9fe", color: "#6d28d9" },
    green: { bg: "#dcfce7", color: "#15803d" },
    amber: { bg: "#fef3c7", color: "#b45309" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "7px",
          bgcolor: t.bg,
          color: t.color,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{ fontSize: 13, color: "#71717a" }}
        >
          {label}:{" "}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#09090b",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
            verticalAlign: "bottom",
            maxWidth: 220,
          }}
          title={String(value)}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Order details card ────────────────────────────────────────────────────

function OrderCard({ bundle, storeDomain }) {
  const order = bundle?.order;
  if (!order) return null;

  const orderName = order.name || `#${order.order_number || order.id || ""}`;
  // Append the order's `note`/title if present so the header reads
  // "Order #1004 - test" like the design.
  const titleSuffix = order.title || order.note || order.email || "";
  const currency = order.currency;
  const total = formatMoney(order.total_price, currency);
  const lineItems = bundle?.line_items || [];
  const subtotal = order.subtotal_price;
  const discount = bundle?.discount_codes?.[0];
  const totalDiscounts = order.total_discounts;
  const shipping =
    order.total_shipping ??
    order.total_shipping_price_set?.shop_money?.amount ??
    "0.00";
  const tax = order.total_tax;
  const customer = order.customer;
  const paidDate = order.processed_at || order.created_at;
  const adminUrl = order.admin_url;
  const status = order.financial_status || "pending";
  const isPaid = String(status).toLowerCase() === "paid";

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Header strip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          p: "10px 14px",
          borderBottom: "1px solid #f4f4f5",
        }}
      >
        <ShopifyMark size={42} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontFamily: FONT_HEADING,
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "#09090b",
              }}
            >
              Order {orderName}
              {titleSuffix ? (
                <Box component="span" sx={{ color: "#3f3f46", fontWeight: 600 }}>
                  {" "}
                  - {titleSuffix}
                </Box>
              ) : null}
            </Typography>
            <Box
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                px: "8px",
                py: "2px",
                borderRadius: "9999px",
                bgcolor: isPaid ? "#dcfce7" : "#fef3c7",
                color: isPaid ? "#166534" : "#92400e",
                textTransform: "capitalize",
              }}
            >
              {status}
            </Box>
            {adminUrl && (
              <Box
                component="a"
                href={adminUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1d4ed8",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  "&:hover": { color: "#1e40af" },
                }}
              >
                View in Shopify
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Box>
            )}
            {storeDomain && (
              <Box
                sx={{
                  fontSize: 12,
                  color: "#1e3a8a",
                  bgcolor: "#dbeafe",
                  border: "1px solid #bfdbfe",
                  px: "8px",
                  py: "2px",
                  borderRadius: "9999px",
                  fontFamily: "monospace",
                  lineHeight: 1.4,
                }}
              >
                {storeDomain}
              </Box>
            )}
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 18,
            fontWeight: 700,
            color: "#09090b",
            letterSpacing: "-0.01em",
            flexShrink: 0,
          }}
        >
          {total}
        </Typography>
      </Box>

      {/* Line items table */}
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 14,
        }}
      >
        <thead>
          <tr>
            {[
              { label: "Product", align: "left" },
              { label: "SKU", align: "left" },
              { label: "Qty", align: "left" },
              { label: "Unit Price", align: "left" },
              { label: "Line Total", align: "left" },
            ].map((h) => (
              <Box
                component="th"
                key={h.label}
                sx={{
                  textAlign: h.align,
                  p: "8px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#52525b",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  bgcolor: "#fafafa",
                  borderBottom: "1px solid #f4f4f5",
                }}
              >
                {h.label}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineItems.length === 0 ? (
            <tr>
              <Box
                component="td"
                colSpan={5}
                sx={{
                  p: "14px 18px",
                  textAlign: "center",
                  color: "#a1a1aa",
                  fontSize: 13,
                }}
              >
                No line items
              </Box>
            </tr>
          ) : (
            lineItems.map((item, i) => {
              const qty = item.quantity ?? 0;
              const unit = item.price;
              const line =
                item.line_total ??
                (unit != null && qty ? parseFloat(unit) * qty : unit);
              const isLast = i === lineItems.length - 1;
              return (
                <tr key={item.id || i}>
                  <Box
                    component="td"
                    sx={{
                      p: "8px 14px",
                      borderBottom: isLast ? "none" : "1px solid #f4f4f5",
                      color: "#09090b",
                    }}
                  >
                    {item.title || item.name}
                    {item.variant_title ? (
                      <Box
                        component="span"
                        sx={{ color: "#71717a", fontSize: 12 }}
                      >
                        {" "}
                        · {item.variant_title}
                      </Box>
                    ) : null}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: "8px 14px",
                      borderBottom: isLast ? "none" : "1px solid #f4f4f5",
                      color: "#71717a",
                    }}
                  >
                    {item.sku || "—"}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: "8px 14px",
                      borderBottom: isLast ? "none" : "1px solid #f4f4f5",
                      color: "#09090b",
                    }}
                  >
                    {qty}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: "8px 14px",
                      borderBottom: isLast ? "none" : "1px solid #f4f4f5",
                      color: "#09090b",
                    }}
                  >
                    {formatMoney(unit, currency)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: "8px 14px",
                      borderBottom: isLast ? "none" : "1px solid #f4f4f5",
                      color: "#09090b",
                    }}
                  >
                    {formatMoney(line, currency)}
                  </Box>
                </tr>
              );
            })
          )}
        </tbody>
      </Box>

      {/* Totals strip */}
      <Box
        sx={{
          bgcolor: "#fafafa",
          borderTop: "1px solid #f4f4f5",
          p: "8px 14px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "2px",
        }}
      >
        {subtotal != null && (
          <SummaryLine label="Subtotal" value={formatMoney(subtotal, currency)} />
        )}
        {discount && (
          <SummaryLine
            label={`Discount${discount.code ? ` (${discount.code})` : ""}`}
            value={`−${formatMoney(discount.amount ?? totalDiscounts, currency)}`}
            valueColor="#dc2626"
          />
        )}
        {parseFloat(shipping) > 0 && (
          <SummaryLine label="Shipping" value={formatMoney(shipping, currency)} />
        )}
        {parseFloat(tax) > 0 && (
          <SummaryLine label="Tax" value={formatMoney(tax, currency)} />
        )}
        <SummaryLine
          label="Total"
          value={total}
          bold
          valueColor="#16a34a"
        />
      </Box>

      {/* Info badges row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(4, 1fr)",
          },
          gap: 1.5,
          p: "10px 14px",
          borderTop: "1px solid #f4f4f5",
        }}
      >
        {paidDate && (
          <InfoBadge
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Paid"
            value={formatDate(paidDate)}
            tone="blue"
          />
        )}
        <InfoBadge
          icon={<HubOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Channel"
          value={order.channel || order.source_name || "Online Store"}
          tone="purple"
        />
        {currency && (
          <InfoBadge
            icon={<LocalAtmOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Currency"
            value={currency}
            tone="green"
          />
        )}
        {customer && (
          <InfoBadge
            icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Customer"
            value={
              [customer.first_name, customer.last_name]
                .filter(Boolean)
                .join(" ") ||
              customer.email ||
              "—"
            }
            tone="amber"
          />
        )}
      </Box>
    </Box>
  );
}

function SummaryLine({ label, value, bold, valueColor }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 4 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: bold ? 700 : 500,
          color: bold ? "#09090b" : "#71717a",
          minWidth: 80,
          textAlign: "right",
        }}
      >
        {label}:
      </Typography>
      <Typography
        sx={{
          fontFamily: bold ? FONT_HEADING : "inherit",
          fontSize: bold ? 14 : 13,
          fontWeight: bold ? 700 : 500,
          color: valueColor || "#09090b",
          minWidth: 110,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ── Fulfillment status card ───────────────────────────────────────────────

function FulfillmentCard({ bundle }) {
  const order = bundle?.order;
  if (!order) return null;
  const fulfillment = bundle?.fulfillments?.[0];

  const paidDone = !!order.financial_status && order.financial_status !== "pending";
  const shippedDone = !!fulfillment;
  const deliveredDone = order.fulfillment_status === "fulfilled";

  const steps = [
    {
      label: "Paid",
      done: paidDone,
      date: paidDone ? formatDate(order.processed_at || order.created_at) : "",
    },
    {
      label: "Shipped",
      done: shippedDone,
      date: shippedDone ? formatDate(fulfillment.created_at) : "",
    },
    {
      label: "Delivered",
      done: deliveredDone,
      date: deliveredDone && fulfillment?.updated_at ? formatDate(fulfillment.updated_at) : "",
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "12px",
        p: "10px 14px 12px",
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_HEADING,
          fontSize: 12,
          fontWeight: 700,
          color: "#71717a",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          mb: 1,
        }}
      >
        Fulfillment Status
      </Typography>

      {/* Connector line + circles — compact horizontal progress */}
      <Box
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
          alignItems: "start",
          pt: "2px",
        }}
      >
        {steps.map((step, i) => {
          const prev = i > 0 ? steps[i - 1] : null;
          const segmentBefore = i > 0;
          const segmentAfter = i < steps.length - 1;
          const beforeDone = prev?.done;
          const afterDone = step.done && steps[i + 1]?.done;

          return (
            <Box
              key={step.label}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2px",
                minWidth: 0,
              }}
            >
              {segmentBefore && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 11,
                    left: 0,
                    right: "50%",
                    height: 0,
                    borderTop: beforeDone
                      ? "2px solid #16a34a"
                      : "1.5px dashed #d4d4d8",
                  }}
                />
              )}
              {segmentAfter && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 11,
                    left: "50%",
                    right: 0,
                    height: 0,
                    borderTop: afterDone
                      ? "2px solid #16a34a"
                      : "1.5px dashed #d4d4d8",
                  }}
                />
              )}

              {/* Circle (smaller: 22px) */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: step.done ? "none" : "2px solid #d4d4d8",
                  bgcolor: step.done ? "#16a34a" : "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  boxShadow: step.done
                    ? "0 0 0 3px #f0fdf4"
                    : "0 0 0 3px #fff",
                }}
              >
                {step.done ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
              </Box>

              {/* Label */}
              <Typography
                sx={{
                  fontFamily: FONT_HEADING,
                  fontSize: 13,
                  fontWeight: 700,
                  color: step.done ? "#16a34a" : "#52525b",
                  mt: "2px",
                  lineHeight: 1.2,
                }}
              >
                {step.label}
              </Typography>

              {/* Date / Pending */}
              <Typography sx={{ fontSize: 12, color: "#71717a", lineHeight: 1.2 }}>
                {step.date || "Pending"}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Tracking number (when available) */}
      {fulfillment?.tracking_number && (
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: "1px solid #f4f4f5",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: 13,
            flexWrap: "wrap",
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: "#09090b" }}>
            {fulfillment.tracking_company || "Carrier"}
          </Box>
          <Box component="span" sx={{ color: "#71717a" }}>
            Tracking:
          </Box>
          <Box
            component="a"
            href={fulfillment.tracking_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "#1d4ed8",
              fontFamily: "monospace",
              fontSize: 13,
              textDecoration: "underline",
              textUnderlineOffset: "2px",
            }}
          >
            {fulfillment.tracking_number}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Entity component ──────────────────────────────────────────────────────

export default function Deal({ data, storeDomain, loading }) {
  const bundle = data || null;

  if (!loading && !bundle?.order) {
    return (
      <Shell storeDomain={storeDomain} loading={false}>
        <EmptyState
          icon="📦"
          title="No order data"
          message="No order details available for this deal."
        />
      </Shell>
    );
  }

  return (
    // No storeDomain passed → Shell hides its top bar entirely; the shop
    // pill is rendered inside the OrderCard header instead.
    <Shell loading={loading} bare>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <OrderCard bundle={bundle} storeDomain={storeDomain} />
        <FulfillmentCard bundle={bundle} />
      </Box>
    </Shell>
  );
}
